import { getContainer } from "../../ioc/IOCSetup";
import { UiSecuritySettings } from "../../services/SecuritySettingsService";

/**
 * Shared utility for handling large amount confirmation flow across multiple components.
 * Provides EUR normalization, conversion, threshold checking, and promise-based confirmation control.
 * 
 * Used by: UserBonusComponent, AssignRewardToUser, UserTransactionPanel
 */

export interface LargeAmountCheckResult {
    requiresConfirmation: boolean;
    euroAmount?: number;
}

export class LargeAmountConfirmationHandler {
    private pendingSubmitResolve?: () => void;
    private pendingSubmitReject?: (reason?: any) => void;
    private submissionInProgress: boolean = false;
    private settingsLoaded: boolean = false;
    private largeAmountThreshold: number = 0;


    trimmedCurrency(currency?: string): string {
        return currency ? currency.trim() : '';
    }
    
    async loadSecuritySettings(settings?: UiSecuritySettings): Promise<void> {
        if (this.settingsLoaded) {
            return;
        }

        try {
            const effectiveSettings = settings ?? await getContainer()
                .getSecuritySettingsService()
                .fetchUiSettings();

            this.largeAmountThreshold = effectiveSettings.largeAmountThreshold ?? 0;
            this.settingsLoaded = true;
        } catch (error) {
            console.error("Failed to load security settings, defaulting to disabled (threshold=0)", error);
            this.largeAmountThreshold = 0;
            this.settingsLoaded = true;
        }
    }

    /**
     * Convert amount to EUR for threshold comparison
     */
    async calculateEuroAmount(amount: number, currency: string): Promise<number> {
        const normalizedCurrency = this.trimmedCurrency(currency);

        if (normalizedCurrency.toUpperCase() === "EUR") {
            return amount;
        }

        try {
            const rate = await getContainer()
                .getCurrencyRateService()
                .getCryptoAndFiatToEuroExchangeRates(normalizedCurrency);
            return amount * rate;
        } catch (error) {
            console.error(`Failed to get exchange rate for ${normalizedCurrency}`, error);
            return amount;
        }
    }

    /**
     * Check if amount exceeds threshold and requires confirmation
     */
    async checkThreshold(amount: number, currency: string): Promise<LargeAmountCheckResult> {
        // Ensure settings are loaded
        if (!this.settingsLoaded) {
            console.warn("Security settings not loaded yet, loading...");
            await this.loadSecuritySettings();
        }

        // If threshold is 0 or not set, no confirmation needed
        if (!this.largeAmountThreshold || this.largeAmountThreshold === 0) {
            return { requiresConfirmation: false };
        }

        // Check submission in progress
        if (this.submissionInProgress) {
            throw new Error("Submission already in progress");
        }

        try {
            const euroAmount = await this.calculateEuroAmount(amount, currency);

            if (euroAmount > this.largeAmountThreshold) {
                return { requiresConfirmation: true, euroAmount };
            }

            return { requiresConfirmation: false, euroAmount };
        } catch (error) {
            console.error("Error checking threshold:", error);
            return { requiresConfirmation: false };
        }
    }

    /**
     * Wait for user confirmation via promise
     * Returns a promise that resolves when user confirms, or never rejects on cancel
     */
    waitForConfirmation(): Promise<void> {
        this.submissionInProgress = true;
        return new Promise<void>((resolve, reject) => {
            this.pendingSubmitResolve = resolve;
            this.pendingSubmitReject = reject;
        });
    }

    /**
     * User confirmed large amount
     */
    confirmLargeAmount(): void {
        if (this.pendingSubmitResolve) {
            this.pendingSubmitResolve();
        }
        this.clearPending();
        this.submissionInProgress = false;
    }

    /**
     * User cancelled large amount
     */
    cancelLargeAmount(): void {
        if (this.pendingSubmitReject) {
            this.pendingSubmitReject(new Error("USER_CANCELLED"));
        }
        this.clearPending();
        this.submissionInProgress = false;
    }

    /**
     * Clean up pending promises
     */
    private clearPending(): void {
        this.pendingSubmitResolve = undefined;
        this.pendingSubmitReject = undefined;
    }

    /**
     * Reset submission state (call on component unmount or error)
     */
    reset(): void {
        this.submissionInProgress = false;
        if (this.pendingSubmitReject) {
            this.pendingSubmitReject("Component unmounted or reset");
        }
        this.clearPending();
    }

    /**
     * Get current threshold value
     */
    getThreshold(): number {
        return this.largeAmountThreshold;
    }

    /**
     * Check if settings are loaded
     */
    isSettingsLoaded(): boolean {
        return this.settingsLoaded;
    }

    /**
     * Check if submission is in progress
     */
    isSubmissionInProgress(): boolean {
        return this.submissionInProgress;
    }

    /**
     * Reset submission in progress flag (for error recovery)
     */
    resetSubmissionFlag(): void {
        this.submissionInProgress = false;
    }
}

